import JSCookie from 'js-cookie';
import { ref, onBeforeUnmount } from 'vue-demi';
import { useOneReachConfig } from './common';

export type Theme = 'light' | 'dark';

/**
 * Track theme changes based on cookies.
 * Allow switching themes using "CMD + Shift + D" key.
 */
export const useAppThemeTracking = () => {
  const ORConfig = useOneReachConfig().getORConfig();
  const THEME_COOKIE_CHECK_INTERVAL = 1000;
  const THEME_DARK_CLASS = 'dark';
  const THEME_COOKIE = `${ORConfig.CLIENT}_${ORConfig.ENV}_dark_mode`;
  const timer = ref(0 as number);

  const getTheme = () => {
    const value = JSCookie.get(THEME_COOKIE) || '0';
    return value === '1' ? 'dark' : 'light';
  };

  const setTheme = (args: { theme: Theme; cookie?: boolean; }) => {
    const { theme, cookie } = args;
    switch (theme) {
      case 'dark':
        document.documentElement.classList.add(THEME_DARK_CLASS);
        break;
      case 'light':
      default:
        document.documentElement.classList.remove(THEME_DARK_CLASS);
        break;
    }

    if (cookie) {
      const value = theme === 'dark' ? '1' : '0';
      JSCookie.set(THEME_COOKIE, value, { domain: ORConfig.COOKIE_DOMAIN });
    }
  };

  const toggleTheme = (e: KeyboardEvent) => {
    const SHORTCUT_MAIN_KEY = 'd';
    if (e.metaKey && e.shiftKey && e.key === SHORTCUT_MAIN_KEY) {
      e.preventDefault();
      const theme = getTheme();
      setTheme({ theme: theme === 'dark' ? 'light' : 'dark', cookie: true });
    }
  };

  const initializeThemeTracking = () => {
    setTheme({ theme: getTheme() });
    document.addEventListener('keydown', toggleTheme);

    timer.value = window.setInterval(() => {
      setTheme({ theme: getTheme() });
    }, THEME_COOKIE_CHECK_INTERVAL);
  };

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', toggleTheme);
    window.clearInterval(timer.value);
  });

  return { initializeThemeTracking };
};
